/**
 * ============================================================================
 * LINK ANALYTICS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Displays link analytics and statistics
 */

import React, { useState, useEffect } from 'react';
import { useLinks } from '../../hooks/useLinks';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import ProgressBar from '../common/ProgressBar';

/**
 * Link Analytics Component
 * @param {Object} props
 * @param {string} props.transactionId - Transaction ID
 * @param {string} props.shortCode - Short code (optional)
 * @returns {JSX.Element}
 */
const LinkAnalytics = ({ transactionId, shortCode }) => {
  const { getLinkStats, loading } = useLinks();
  const { showError } = useToast();
  const { language } = useI18n();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      if (!transactionId && !shortCode) return;
      try {
        setIsLoading(true);
        const data = await getLinkStats(transactionId || shortCode);
        setStats(data);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement des statistiques' : 'Failed to load statistics')
        );
        console.error('Load stats error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, [transactionId, shortCode, getLinkStats, showError, language]);

  if (isLoading || loading) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <Spinner size="md" />
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
          {language === 'fr' ? 'Chargement des statistiques...' : 'Loading statistics...'}
        </p>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          {language === 'fr' ? 'Aucune statistique disponible' : 'No statistics available'}
        </p>
      </Card>
    );
  }

  const totalViews = stats.totalViews || stats.views || 0;
  const totalShares = stats.totalShares || stats.shares || 0;
  const uniqueViews = stats.uniqueViews || stats.unique || 0;
  const shareChannels = stats.shareChannels || stats.channels || {};
  const viewHistory = stats.viewHistory || stats.history || [];

  const topChannels = Object.entries(shareChannels)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <h3 className="text-xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <span className="text-2xl">📊</span>
        {language === 'fr' ? 'Statistiques du lien' : 'Link Analytics'}
      </h3>

      <div className="space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card variant="outlined" className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800 p-4 text-center">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Vues totales' : 'Total Views'}
            </p>
            <p className="text-2xl font-black text-blue-600 dark:text-blue-400">
              {totalViews}
            </p>
          </Card>
          <Card variant="outlined" className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800 p-4 text-center">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Partages' : 'Shares'}
            </p>
            <p className="text-2xl font-black text-green-600 dark:text-green-400">
              {totalShares}
            </p>
          </Card>
          <Card variant="outlined" className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800 p-4 text-center">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Vues uniques' : 'Unique Views'}
            </p>
            <p className="text-2xl font-black text-purple-600 dark:text-purple-400">
              {uniqueViews}
            </p>
          </Card>
        </div>

        {/* Share Channels */}
        {topChannels.length > 0 && (
          <div>
            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Canaux de partage' : 'Share Channels'}
            </h4>
            <div className="space-y-2">
              {topChannels.map(([channel, count]) => {
                const percentage = totalShares > 0 ? (count / totalShares) * 100 : 0;
                const channelIcons = {
                  whatsapp: '📱',
                  email: '📧',
                  sms: '💬',
                  facebook: '📘',
                  twitter: '🐦',
                  instagram: '📷',
                  clipboard: '📋',
                };
                return (
                  <div key={channel} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 font-medium text-gray-700 dark:text-gray-300">
                        <span>{channelIcons[channel] || '🔗'}</span>
                        {channel.charAt(0).toUpperCase() + channel.slice(1)}
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white">{count}</span>
                    </div>
                    <ProgressBar
                      value={percentage}
                      variant="info"
                      animated
                      showLabel={false}
                      className="h-2"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* View History */}
        {viewHistory.length > 0 && (
          <div>
            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Historique récent' : 'Recent History'}
            </h4>
            <div className="space-y-2">
              {viewHistory.slice(0, 5).map((entry, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  className="p-3 bg-gray-50 dark:bg-gray-800/50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {entry.type === 'view' ? '👁️' : '📤'}
                      </span>
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {entry.type === 'view'
                            ? language === 'fr'
                              ? 'Vue'
                              : 'View'
                            : language === 'fr'
                            ? 'Partage'
                            : 'Share'}
                        </p>
                        {entry.channel && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            via {entry.channel}
                          </p>
                        )}
                      </div>
                    </div>
                    {entry.timestamp && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(entry.timestamp).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default LinkAnalytics;
